// SnapshotBand — a whole-business "at a glance" headline for the Explore landing
// screen. Unfiltered (full portfolio), loads once from /api/wf/overview. Distinct
// from the filtered explorer below it. Silently hides if no snapshot is published.

import { useEffect, useState } from "react";
import { InsightsApi } from "../hooks/useApi.js";
import { CountUp, HBars } from "./Charts.jsx";
import Icon from "./Icon.jsx";
import { fmtRm, fmtN } from "../lib/format.js";

export default function SnapshotBand({ filter, runId }) {
  const [d, setD] = useState(null);
  const [hide, setHide] = useState(false);

  // Refetch whenever ANY active filter dimension or the pinned run changes. The
  // overview honours customer + date across all streams and category/salesperson/
  // product/branch on the streams that carry them (invoice/order-derived tiles), so
  // the band reflects the full current selection — matching the compare/breakdown cards.
  const fkey = JSON.stringify({
    c: filter?.campuses ?? [], cat: filter?.categories ?? [], s: filter?.sources ?? [],
    p: filter?.products ?? [], b: filter?.branches ?? [],
    f: filter?.from ?? "", t: filter?.to ?? "", r: runId ?? "",
  });
  useEffect(() => {
    setHide(false);
    InsightsApi.overview(filter ?? undefined, runId).then(setD).catch(() => setHide(true));
  }, [fkey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (hide || !d) return null;
  const filtered = !!(
    filter?.campuses?.length || filter?.categories?.length || filter?.sources?.length ||
    filter?.products?.length || filter?.branches?.length || filter?.from || filter?.to
  );

  const years = d.revenueByYear ?? [];
  const totalRev = years.reduce((s, r) => s + (r.revenue_ex_vat || 0), 0);
  const margin = d.margin || {};
  const marginPct = margin.revenue_ex_vat ? (margin.gross_margin / margin.revenue_ex_vat) * 100 : null;
  const backlog = d.openBacklog || {};
  const funnel = d.funnel || {};
  const topCust = (d.topCustomers || [])[0];

  const tiles = [
    { ic: "spark", c: "c1", v: totalRev, fmt: fmtRm, l: "Total revenue (ex-VAT)" },
    { ic: "card", c: "c2", v: margin.gross_margin ?? 0, fmt: fmtRm, l: marginPct != null ? `Gross margin · ${marginPct.toFixed(0)}%` : "Gross margin" },
    { ic: "layers", c: "c3", v: backlog.open_value ?? 0, fmt: fmtRm, l: `Open backlog · ${fmtN(backlog.open_orders ?? 0)} orders` },
    { ic: "check", c: "c4", v: funnel.invoiced_pct ?? 0, fmt: (x) => `${x.toFixed(0)}%`, l: "Orders invoiced (fulfilment)" },
  ];

  const yearRows = years.map((r) => ({ key: String(r.year), spend: r.revenue_ex_vat || 0, lines: r.lines || 0 }));

  return (
    <div className="panel">
      <div className="eyebrow"><Icon name="sparkles" size={13} /> Business at a glance · {filtered ? "current selection" : "full portfolio"}</div>
      <div className="kpis stagger" style={{ marginTop: 12 }}>
        {tiles.map((k) => (
          <div className={`kpi ${k.c}`} key={k.l}>
            <div className="ic"><Icon name={k.ic} size={18} /></div>
            <div className="v"><CountUp value={k.v} fmt={k.fmt} /></div>
            <div className="l">{k.l}</div>
          </div>
        ))}
      </div>
      <div className="viz-grid two" style={{ marginTop: 16 }}>
        <div className="viz-card">
          <h3>Revenue by year</h3>
          <div className="sub">Full-year FY2024 / FY2025 · 2026 YTD</div>
          <HBars rows={yearRows} topN={5} colorByIndex />
        </div>
        <div className="viz-card">
          <h3>Largest customer</h3>
          <div className="sub">By revenue · {filtered ? "current selection" : "all-time"}</div>
          {topCust ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 6 }}>
              <div style={{ font: "700 22px var(--font-head)", color: "var(--ink)" }}>{topCust.customer}</div>
              <div style={{ font: "600 18px var(--font-head)", color: "var(--curro-blue)" }}>{fmtRm(topCust.revenue_ex_vat)}</div>
              <div className="hint">{totalRev ? ((topCust.revenue_ex_vat / totalRev) * 100).toFixed(1) : "0"}% of total revenue</div>
            </div>
          ) : <div className="hint">No data.</div>}
        </div>
      </div>
    </div>
  );
}
